(() => {
  'use strict';

  const PUBLIC_ENDPOINT = '/apps/ots-offers/reviews/public';
  const ALLOWED_PLACEMENTS = new Set(['reviews', 'home', 'product']);
  const ALLOWED_SORTS = new Set(['newest', 'highest', 'lowest']);
  const ALLOWED_CATEGORIES = new Set([
    'Electronics & Computing',
    'Music & Audio',
    'Auto & Motorcycle',
    'Home & Other Finds',
    'Apparel & Footwear',
  ]);

  const conditionLabels = {
    BETTER: 'Better than expected',
    AS_EXPECTED: 'As expected',
    WORSE: 'Worse than expected',
  };

  const makeElement = (tagName, className, text) => {
    const element = document.createElement(tagName);
    if (className) element.className = className;
    if (typeof text === 'string') element.textContent = text;
    return element;
  };

  const textValue = (value, maxLength = 500) => {
    if (typeof value !== 'string') return '';
    return value.trim().slice(0, maxLength);
  };

  const integerValue = (value, minimum, maximum) => {
    const parsed = Number(value);
    if (!Number.isInteger(parsed) || parsed < minimum || parsed > maximum) return null;
    return parsed;
  };

  const decimalValue = (value, minimum, maximum) => {
    const parsed = Number(value);
    if (!Number.isFinite(parsed) || parsed < minimum || parsed > maximum) return null;
    return parsed;
  };

  const safeUrl = (value, allowExternalImage = false) => {
    const raw = textValue(value, 2048);
    if (!raw) return null;

    try {
      const parsed = new URL(raw, window.location.origin);
      const sameOrigin = parsed.origin === window.location.origin;
      if (!sameOrigin && (!allowExternalImage || parsed.protocol !== 'https:')) return null;
      if (sameOrigin && !['http:', 'https:'].includes(parsed.protocol)) return null;
      return parsed.href;
    } catch (_error) {
      return null;
    }
  };

  const safeAnchor = (value, fallbackId) => {
    const raw = textValue(value, 110).replace(/^#/, '');
    if (/^review-[A-Za-z0-9][A-Za-z0-9_-]{2,96}$/.test(raw)) return raw;

    const cleanId = textValue(fallbackId, 90).replace(/[^A-Za-z0-9_-]/g, '');
    if (cleanId) return `review-${cleanId}`;
    return '';
  };

  const validPhoto = (photo, productTitle) => {
    if (!photo || typeof photo !== 'object') return null;
    const url = safeUrl(photo.url, false);
    if (!url) return null;
    const path = new URL(url).pathname;
    if (!/^\/apps\/ots-offers\/reviews\/media\/[0-9a-fA-F-]{36}$/.test(path)) return null;
    return {
      url,
      alt: textValue(photo.alt, 240) || `Customer review photo for ${productTitle}`,
    };
  };

  const validReview = (review) => {
    if (!review || typeof review !== 'object' || review.verifiedPurchase !== true) return null;

    const id = textValue(review.id, 100);
    const rating = integerValue(review.rating, 1, 5);
    const title = textValue(review.title, 180);
    const body = textValue(review.body, 5000);
    const displayName = textValue(review.displayName, 100);
    const product = review.product && typeof review.product === 'object' ? review.product : null;
    const productTitle = textValue(product?.title, 240);
    const anchor = safeAnchor(review.anchor, id);

    if (!id || !rating || !title || !body || !displayName || !productTitle || !anchor) return null;

    const photos = Array.isArray(review.photos)
      ? review.photos.slice(0, 6).map((photo) => validPhoto(photo, productTitle)).filter(Boolean)
      : [];

    return {
      id,
      anchor,
      rating,
      title,
      body,
      displayName,
      conditionMatch: Object.hasOwn(conditionLabels, review.conditionMatch) ? review.conditionMatch : null,
      publishedAt: textValue(review.publishedAt, 80),
      purchaseMonth: textValue(review.purchaseMonth, 80),
      product: {
        title: productTitle,
        category: textValue(product.category, 120),
        condition: textValue(product.condition, 100),
        testingStatus: textValue(product.testingStatus, 120),
        imageUrl: safeUrl(product.imageUrl, true),
        imageAlt: textValue(product.imageAlt, 240) || `${productTitle} purchased item`,
        productUrl: safeUrl(product.productUrl, false),
        sold: product.sold === true,
      },
      photos,
    };
  };

  const monthLabel = (review) => {
    if (review.purchaseMonth) return review.purchaseMonth;
    if (!review.publishedAt) return '';

    const date = new Date(review.publishedAt);
    if (Number.isNaN(date.getTime())) return '';
    return new Intl.DateTimeFormat(document.documentElement.lang || 'en', {
      month: 'long',
      year: 'numeric',
    }).format(date);
  };

  const ratingStars = (rating, className = '') => {
    const stars = makeElement('span', `ots-rating-stars${className ? ` ${className}` : ''}`);
    stars.style.setProperty('--ots-rating-percent', `${Math.max(0, Math.min(100, rating * 20))}%`);
    stars.setAttribute('role', 'img');
    stars.setAttribute('aria-label', `Rated ${rating} out of 5`);
    return stars;
  };

  class OtsReviewsFeed extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.endpoint = this.dataset.endpoint === PUBLIC_ENDPOINT ? PUBLIC_ENDPOINT : '';
      this.placement = ALLOWED_PLACEMENTS.has(this.dataset.placement) ? this.dataset.placement : 'reviews';
      this.limit = integerValue(this.dataset.limit, 1, 24) || (this.placement === 'reviews' ? 12 : 3);
      this.autoHide = this.dataset.autoHide === 'true';
      this.showEmpty = this.dataset.showEmpty !== 'false';
      this.offset = 0;
      this.abortController = null;
      this.sectionContainer = this.closest('.ots-reviews-section');

      this.summary = this.querySelector('[data-reviews-summary]');
      this.list = this.querySelector('[data-review-list]');
      this.loading = this.querySelector('[data-reviews-loading]');
      this.error = this.querySelector('[data-reviews-error]');
      this.empty = this.querySelector('[data-reviews-empty]');
      this.emptyHeading = this.querySelector('[data-reviews-empty-heading]');
      this.emptyText = this.querySelector('[data-reviews-empty-text]');
      this.status = this.querySelector('[data-reviews-status]');
      this.moreButton = this.querySelector('[data-reviews-more]');
      this.retryButton = this.querySelector('[data-reviews-retry]');
      this.filters = this.querySelector('[data-review-filters]');
      this.categoryControl = this.querySelector('[data-review-category]');
      this.ratingControl = this.querySelector('[data-review-rating]');
      this.sortControl = this.querySelector('[data-review-sort]');

      this.permalinkReview = this.readPermalink();

      this.filters?.addEventListener('submit', (event) => {
        event.preventDefault();
        this.permalinkReview = '';
        this.load({ reset: true });
      });

      this.filters?.addEventListener('reset', () => {
        window.setTimeout(() => {
          this.permalinkReview = '';
          this.load({ reset: true });
        }, 0);
      });

      this.moreButton?.addEventListener('click', () => {
        this.permalinkReview = '';
        this.load({ reset: false });
      });

      this.retryButton?.addEventListener('click', () => this.load({ reset: this.list?.children.length === 0 }));

      if (!this.endpoint || !this.list) {
        this.handleError();
        return;
      }

      this.load({ reset: true });
    }

    disconnectedCallback() {
      this.abortController?.abort();
    }

    readPermalink() {
      if (this.placement !== 'reviews') return '';
      const hash = window.location.hash.replace(/^#/, '');
      if (!/^review-OTS-RV-[A-Za-z0-9_-]{1,70}$/.test(hash)) return '';
      return hash.replace(/^review-/, '');
    }

    currentFilters() {
      const category = textValue(this.categoryControl?.value || this.dataset.category, 120);
      const rating = integerValue(this.ratingControl?.value, 1, 5);
      const sort = ALLOWED_SORTS.has(this.sortControl?.value) ? this.sortControl.value : 'newest';

      return {
        category: ALLOWED_CATEGORIES.has(category) ? category : '',
        rating,
        sort,
      };
    }

    requestUrl(reset) {
      const url = new URL(this.endpoint, window.location.origin);
      const filters = this.currentFilters();
      url.searchParams.set('limit', String(this.limit));
      url.searchParams.set('offset', String(reset ? 0 : this.offset));
      url.searchParams.set('placement', this.placement);
      if (filters.category) url.searchParams.set('category', filters.category);
      if (filters.rating) url.searchParams.set('rating', String(filters.rating));
      if (filters.sort !== 'newest') url.searchParams.set('sort', filters.sort);
      if (reset && this.permalinkReview && this.placement === 'reviews') {
        url.searchParams.set('review', this.permalinkReview);
      }
      return url;
    }

    setBusy(busy, showLoading = false) {
      if (this.loading) this.loading.hidden = !showLoading;
      this.querySelectorAll('button, select').forEach((control) => {
        control.disabled = busy;
      });
      this.setAttribute('aria-busy', String(busy));
    }

    async load({ reset }) {
      this.abortController?.abort();
      const controller = new AbortController();
      this.abortController = controller;

      const showLoading = reset && this.list.children.length === 0;
      this.error.hidden = true;
      if (reset) this.empty.hidden = true;
      this.setBusy(true, showLoading);

      try {
        const response = await fetch(this.requestUrl(reset), {
          method: 'GET',
          credentials: 'same-origin',
          headers: { Accept: 'application/json' },
          signal: controller.signal,
        });

        if (!response.ok) throw new Error('Reviews request failed');
        const payload = await response.json();
        if (!payload || payload.ok !== true || !payload.summary || !Array.isArray(payload.reviews)) {
          throw new Error('Invalid reviews response');
        }

        this.renderResponse(payload, reset);
      } catch (error) {
        if (error.name !== 'AbortError' && this.abortController === controller) this.handleError();
      } finally {
        if (!controller.signal.aborted && this.abortController === controller) this.setBusy(false, false);
      }
    }

    renderResponse(payload, reset) {
      const rawReviews = payload.reviews;
      const reviews = rawReviews.map(validReview).filter(Boolean);
      const summaryCount = integerValue(payload.summary.reviewCount, 0, 1000000) ?? 0;

      if (reset) {
        this.list.replaceChildren();
        this.offset = 0;
      }

      const fragment = document.createDocumentFragment();
      reviews.forEach((review) => fragment.append(this.reviewCard(review)));
      this.list.append(fragment);
      this.offset += rawReviews.length;

      this.renderSummary(payload.summary);

      const hasAnyCards = this.list.children.length > 0;
      const hasReviews = summaryCount > 0 && hasAnyCards;
      const hasMore = this.placement === 'reviews' && payload.pagination?.hasMore === true && !this.permalinkReview;
      if (this.moreButton) this.moreButton.hidden = !hasMore;

      if (this.autoHide) {
        this.hidden = !hasReviews;
        if (this.sectionContainer) this.sectionContainer.hidden = !hasReviews;
      } else {
        this.hidden = false;
        if (this.sectionContainer) this.sectionContainer.hidden = false;
      }

      if (reset && !hasAnyCards && !this.autoHide && this.showEmpty) {
        const filters = this.currentFilters();
        const filtered = Boolean(filters.category || filters.rating || filters.sort !== 'newest');
        if (filtered) {
          this.emptyHeading.textContent = 'No reviews match those filters.';
          this.emptyText.textContent = 'Try another department, rating, or sort option.';
        }
        this.empty.hidden = false;
      } else {
        this.empty.hidden = true;
      }

      this.error.hidden = true;
      this.status.textContent = hasAnyCards
        ? `${this.list.children.length} verified ${this.list.children.length === 1 ? 'review' : 'reviews'} loaded.`
        : 'No verified reviews are available.';

      if (reset && this.permalinkReview) this.focusPermalink();
    }

    renderSummary(summary) {
      if (!this.summary) return;

      const count = integerValue(summary.reviewCount, 0, 1000000) ?? 0;
      const average = decimalValue(summary.averageRating, 1, 5);
      if (count === 0 || average === null) {
        this.summary.hidden = true;
        return;
      }

      const roundedAverage = Math.round(average * 10) / 10;
      const averageNode = this.querySelector('[data-summary-average]');
      const countNode = this.querySelector('[data-summary-count]');
      const countLabel = this.querySelector('[data-summary-count-label]');
      const starsNode = this.querySelector('[data-summary-stars]');
      const ratingText = this.querySelector('[data-summary-rating-text]');

      averageNode.textContent = roundedAverage.toFixed(1);
      countNode.textContent = new Intl.NumberFormat().format(count);
      countLabel.textContent = count === 1 ? 'verified purchase' : 'verified purchases';
      starsNode.style.setProperty('--ots-rating-percent', `${roundedAverage * 20}%`);
      ratingText.textContent = `Rated ${roundedAverage.toFixed(1)} out of 5`;

      const condition = summary.conditionMetric;
      const conditionRow = this.querySelector('[data-summary-condition]');
      const percentage = decimalValue(condition?.percentage, 0, 100);
      const sampleSize = integerValue(condition?.sampleSize, 1, 1000000);
      if (conditionRow && percentage !== null && sampleSize !== null) {
        this.querySelector('[data-summary-condition-percentage]').textContent = `${Math.round(percentage)}%`;
        this.querySelector('[data-summary-condition-sample]').textContent = `Based on ${new Intl.NumberFormat().format(sampleSize)} condition responses.`;
        conditionRow.hidden = false;
      } else if (conditionRow) {
        conditionRow.hidden = true;
      }

      this.renderDistribution(summary.distribution, count);
      this.summary.hidden = false;
    }

    renderDistribution(distribution, total) {
      const container = this.querySelector('[data-rating-distribution]');
      if (!container || !distribution || typeof distribution !== 'object') return;

      container.replaceChildren();
      for (let rating = 5; rating >= 1; rating -= 1) {
        const count = integerValue(distribution[rating], 0, 1000000) ?? 0;
        const percentage = total > 0 ? Math.min(100, (count / total) * 100) : 0;
        const row = makeElement('div', 'ots-rating-distribution__row');
        row.setAttribute('role', 'group');
        row.setAttribute('aria-label', `${rating} stars: ${count} ${count === 1 ? 'review' : 'reviews'}`);

        row.append(makeElement('span', '', `${rating} stars`));
        const track = makeElement('span', 'ots-rating-distribution__track');
        track.setAttribute('role', 'progressbar');
        track.setAttribute('aria-label', `${rating}-star reviews`);
        track.setAttribute('aria-valuemin', '0');
        track.setAttribute('aria-valuemax', String(total));
        track.setAttribute('aria-valuenow', String(count));
        const bar = makeElement('span', 'ots-rating-distribution__bar');
        bar.style.setProperty('--ots-distribution-percent', `${percentage}%`);
        track.append(bar);
        row.append(track, makeElement('span', 'ots-rating-distribution__count', String(count)));
        container.append(row);
      }
      container.hidden = false;
    }

    reviewCard(review) {
      const listItem = makeElement('li', 'ots-review-list__item');
      const card = makeElement('article', 'ots-review-card');
      card.id = review.anchor;
      card.tabIndex = -1;

      const topLine = makeElement('div', 'ots-review-card__topline');
      topLine.append(ratingStars(review.rating));
      topLine.append(makeElement('span', 'ots-review-card__verified', 'Verified Purchase'));
      card.append(topLine);

      const titleTag = this.placement === 'reviews' ? 'h2' : 'h3';
      const title = makeElement(titleTag, 'ots-review-card__title', review.title);
      const body = makeElement('p', 'ots-review-card__body', review.body);
      card.append(title, body, this.productContext(review));

      if (review.conditionMatch) {
        const condition = makeElement('p', 'ots-review-card__condition-match');
        condition.append(makeElement('strong', '', 'Condition match:'));
        condition.append(document.createTextNode(` ${conditionLabels[review.conditionMatch]}`));
        card.append(condition);
      }

      if (review.photos.length) card.append(this.customerPhotos(review));

      const footer = makeElement('footer', 'ots-review-card__footer');
      const attribution = makeElement('span');
      attribution.append(makeElement('span', 'ots-review-card__author', review.displayName));
      const month = monthLabel(review);
      if (month) attribution.append(document.createTextNode(` \u00b7 ${month}`));

      const permalink = makeElement('a', 'ots-review-card__permalink', 'Link to this review');
      permalink.href = this.placement === 'reviews'
        ? `#${review.anchor}`
        : `/pages/reviews#${review.anchor}`;
      permalink.setAttribute('aria-label', `Permanent link to review: ${review.title}`);
      footer.append(attribution, permalink);
      card.append(footer);

      listItem.append(card);
      return listItem;
    }

    productContext(review) {
      const context = makeElement('div', 'ots-review-card__context');
      if (review.product.imageUrl) {
        const imageWrap = makeElement('span', 'ots-review-card__product-image');
        const image = document.createElement('img');
        image.src = review.product.imageUrl;
        image.alt = review.product.imageAlt;
        image.loading = 'lazy';
        image.width = 144;
        image.height = 144;
        image.addEventListener('error', () => {
          imageWrap.remove();
          context.classList.add('ots-review-card__context--no-image');
        }, { once: true });
        imageWrap.append(image);
        context.append(imageWrap);
      } else {
        context.classList.add('ots-review-card__context--no-image');
      }

      const details = makeElement('div', 'ots-review-card__product-details');
      const labelRow = makeElement('div', 'ots-review-card__product-meta');
      labelRow.append(makeElement('span', 'ots-review-card__product-label', 'Purchased item'));
      if (review.product.sold) labelRow.append(makeElement('span', 'ots-review-card__sold', 'Sold Find'));
      details.append(labelRow);

      const title = makeElement('p', 'ots-review-card__product-title');
      if (review.product.productUrl) {
        const link = makeElement('a', '', review.product.title);
        link.href = review.product.productUrl;
        title.append(link);
      } else {
        title.textContent = review.product.title;
      }
      details.append(title);

      const metadata = makeElement('p', 'ots-review-card__product-meta');
      if (review.product.category) metadata.append(makeElement('span', '', review.product.category));
      if (review.product.condition) metadata.append(makeElement('span', '', `Condition: ${review.product.condition}`));
      if (review.product.testingStatus) metadata.append(makeElement('span', '', `Testing: ${review.product.testingStatus}`));
      if (metadata.children.length) details.append(metadata);

      context.append(details);
      return context;
    }

    customerPhotos(review) {
      const region = makeElement('div', 'ots-review-card__photos');
      region.append(makeElement('p', 'ots-review-card__photos-title', 'Customer review photos'));
      const list = makeElement('ul', 'ots-review-card__photo-list');

      review.photos.forEach((photo) => {
        const item = makeElement('li');
        const link = makeElement('a', 'ots-review-card__photo');
        link.href = photo.url;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        link.setAttribute('aria-label', `${photo.alt} (opens full-size image in a new tab)`);

        const image = document.createElement('img');
        image.src = photo.url;
        image.alt = photo.alt;
        image.loading = 'lazy';
        image.width = 192;
        image.height = 192;
        image.addEventListener('error', () => item.remove(), { once: true });
        link.append(image);
        item.append(link);
        list.append(item);
      });

      region.append(list);
      return region;
    }

    focusPermalink() {
      const anchor = `review-${this.permalinkReview}`;
      const target = Array.from(this.list.querySelectorAll('.ots-review-card')).find((card) => card.id === anchor);
      if (!target) return;

      window.requestAnimationFrame(() => {
        target.focus({ preventScroll: true });
        target.scrollIntoView({ behavior: 'auto', block: 'start' });
      });
    }

    handleError() {
      this.loading.hidden = true;
      if (this.moreButton) this.moreButton.hidden = true;
      if (this.autoHide && this.list.children.length === 0) {
        this.hidden = true;
        if (this.sectionContainer) this.sectionContainer.hidden = true;
      } else {
        this.hidden = false;
        if (this.sectionContainer) this.sectionContainer.hidden = false;
        this.error.hidden = false;
      }
      this.status.textContent = 'Verified reviews could not be loaded.';
    }
  }

  class OtsReviewProof extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      if (this.dataset.endpoint !== PUBLIC_ENDPOINT) return;

      const url = new URL(PUBLIC_ENDPOINT, window.location.origin);
      url.searchParams.set('limit', '1');
      url.searchParams.set('offset', '0');
      url.searchParams.set('placement', 'reviews');

      fetch(url, {
        method: 'GET',
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      })
        .then((response) => {
          if (!response.ok) throw new Error('Review proof request failed');
          return response.json();
        })
        .then((payload) => {
          const metric = payload?.ok === true ? payload.summary?.conditionMetric : null;
          const percentage = decimalValue(metric?.percentage, 0, 100);
          const sampleSize = integerValue(metric?.sampleSize, 1, 1000000);
          const value = this.querySelector('[data-review-proof-value]');
          if (percentage === null || sampleSize === null || !value) return;

          value.textContent = `${Math.round(percentage)}% of ${new Intl.NumberFormat().format(sampleSize)} verified buyers said their item's condition matched or exceeded the listing.`;
          this.hidden = false;
        })
        .catch(() => {
          this.hidden = true;
        });
    }
  }

  if (!customElements.get('ots-reviews-feed')) customElements.define('ots-reviews-feed', OtsReviewsFeed);
  if (!customElements.get('ots-review-proof')) customElements.define('ots-review-proof', OtsReviewProof);
})();
